import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { ArrowLeft, Search, ShoppingCart, Star, Package, User, LogIn, Settings, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../config';
import { useCurrency } from '../contexts/CurrencyContext';
import Footer from './Footer';

const ProductsPage = ({ navigateHome, navigateToProduct, navigateToCart, navigateToProfile, cart, addToCart }) => {
  const { user, isAuthenticated, loading: authLoading, loginWithGoogle } = useAuth();
  const { formatPrice } = useCurrency();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    manufacturer: '',
    product_type: '',
    min_price: '',
    max_price: ''
  });

  // Load products when component mounts or filters change
  useEffect(() => {
    loadProducts();
  }, [currentPage, filters]);

  // Load categories and manufacturers
  useEffect(() => {
    loadCategories();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '12'
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.manufacturer) params.append('manufacturer', filters.manufacturer);
      if (filters.product_type) params.append('product_type', filters.product_type);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);

      const response = await fetch(`${BACKEND_URL}/api/products?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
        setTotalPages(data.total_pages);
      } else {
        console.error('Failed to load products');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/categories`);
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
        setManufacturers(data.manufacturers);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    // Convert "all" to empty string for backend filtering
    const filterValue = value === 'all' ? '' : value;
    setFilters({ ...filters, [key]: filterValue });
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getStockBadge = (quantity) => {
    if (quantity === 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (quantity < 5) {
      return <Badge variant="outline" className="border-brand-orange-500 text-brand-orange-600">Low Stock ({quantity})</Badge>;
    } else {
      return <Badge variant="outline" className="border-green-500 text-green-600">In Stock ({quantity})</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header with Main Navigation */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            {/* Logo with minimum width */}
            <button 
              onClick={navigateHome}
              className="flex items-center flex-shrink-0"
            >
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </button>

            {/* Spacer matching menu item spacing */}
            <div className="w-8 flex-shrink-0 hidden lg:block"></div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8 items-center">
              <button onClick={() => window.location.href = '/services'} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Services</button>
              <span className="font-medium text-[#0007b5]">Shop</span>
              <button onClick={() => window.location.href = '/marketplace'} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Designs</button>
              <button onClick={() => window.location.href = '/about'} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">About</button>
              <button onClick={() => window.location.href = '/#contact'} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Contact</button>
              <button onClick={() => window.location.href = '/'} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Home</button>
            </nav>

            {/* Right-aligned Cart and Auth buttons for Desktop */}
            <div className="hidden lg:flex items-center space-x-4 ml-auto">
              {/* Shopping Cart */}
              <button 
                onClick={navigateToCart}
                className="relative flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#0007b5] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
                <span className="hidden sm:inline">Cart</span>
              </button>
              
              {/* Firebase Authentication */}
              {authLoading ? (
                <div className="flex items-center space-x-2 font-medium text-slate-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0007b5]"></div>
                  <span>Loading...</span>
                </div>
              ) : isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => navigateToProfile ? navigateToProfile() : window.location.href = '/profile'}
                    className="flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{user?.name || 'Profile'}</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={loginWithGoogle}
                  className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center gap-3 ml-auto">
              {/* Cart for mobile */}
              <button 
                onClick={navigateToCart}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Shopping cart"
              >
                <ShoppingCart className="w-6 h-6 text-slate-600" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#0007b5] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {cart.length}
                  </span>
                )}
              </button>

              {/* Hamburger menu button */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-600" />
                ) : (
                  <div className="space-y-1.5">
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Panel */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-slate-100 pt-4">
              <nav className="flex flex-col space-y-4">
                <button onClick={() => {window.location.href = '/services'; setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Services</button>
                <span className="text-left font-medium text-[#0007b5] py-2">Shop</span>
                <button onClick={() => {window.location.href = '/marketplace'; setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Designs</button>
                <button onClick={() => {window.location.href = '/about'; setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">About</button>
                <button onClick={() => {window.location.href = '/#contact'; setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Contact</button>
                <button onClick={() => {window.location.href = '/'; setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Home</button>

                <div className="border-t border-slate-200 my-2"></div>

                {isAuthenticated ? (
                  <>
                    <button onClick={() => {navigateToProfile ? navigateToProfile() : window.location.href = '/profile'; setMobileMenuOpen(false);}} className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors">
                      <User className="w-4 h-4" />
                      <span>{user?.name || 'Profile'}</span>
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => {loginWithGoogle(); setMobileMenuOpen(false);}}
                    className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 w-full"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">3D Printing</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Shop
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Professional 3D printers, filaments, and accessories
              </p>
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={filters.category} onValueChange={(value) => handleFilterChange('category', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.manufacturer} onValueChange={(value) => handleFilterChange('manufacturer', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {manufacturers.map(manufacturer => (
                  <SelectItem key={manufacturer} value={manufacturer}>{manufacturer}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.product_type} onValueChange={(value) => handleFilterChange('product_type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="FDM Printer">3D Printers (FDM)</SelectItem>
                <SelectItem value="Resin Printer">3D Printers (Resin)</SelectItem>
                <SelectItem value="Filament">Filaments</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex space-x-2">
              <Input
                type="number"
                placeholder="Min Price"
                value={filters.min_price}
                onChange={(e) => handleFilterChange('min_price', e.target.value)}
                className="w-full"
              />
              <Input
                type="number"
                placeholder="Max Price"
                value={filters.max_price}
                onChange={(e) => handleFilterChange('max_price', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex justify-end">
              <Button 
                onClick={() => {
                  setFilters({
                    search: '',
                    category: '',
                    manufacturer: '',
                    product_type: '',
                    min_price: '',
                    max_price: ''
                  });
                  setCurrentPage(1);
                }}
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="w-full h-48 bg-slate-200 rounded-lg mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded mb-4"></div>
                  <div className="h-8 bg-slate-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(product => (
              <Card key={product.id} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md overflow-hidden cursor-pointer">
                <div className="relative" onClick={() => navigateToProduct(product)}>
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                      <Package className="w-16 h-16 text-slate-400" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    {getStockBadge(product.stock_quantity)}
                  </div>
                </div>
                
                <CardHeader className="pb-3" onClick={() => navigateToProduct(product)}>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline" className="text-xs">{product.manufacturer}</Badge>
                    {product.rating && (
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs text-slate-600">{product.rating}</span>
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-lg leading-tight">{product.name}</CardTitle>
                  <CardDescription className="text-sm line-clamp-2">{product.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="pt-0">
                  {/* Key Specifications */}
                  {product.specifications && (
                    <div className="mb-4 text-xs text-slate-600 space-y-1">
                      {product.specifications.build_volume && (
                        <div>Build: {product.specifications.build_volume}</div>
                      )}
                      {product.specifications.material_compatibility && (
                        <div>Material: {product.specifications.material_compatibility}</div>
                      )}
                      {product.specifications.diameter && (
                        <div>Diameter: {product.specifications.diameter}</div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-slate-800">{formatPrice(product.price)}</div>
                      {product.review_count > 0 && (
                        <div className="text-xs text-slate-500">({product.review_count} reviews)</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 space-y-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToProduct(product);
                      }}
                      variant="outline"
                      className="w-full"
                      size="sm"
                    >
                      View Details
                    </Button>
                    
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product);
                        alert(`${product.name} added to cart!`);
                      }}
                      disabled={product.stock_quantity === 0}
                      className={`w-full ${
                        product.stock_quantity === 0 
                          ? 'bg-slate-300 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900'
                      }`}
                      size="sm"
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      {product.stock_quantity === 0 ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-600 mb-2">No products found</h3>
            <p className="text-slate-500">Try adjusting your filters or search terms</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant="outline"
            >
              Previous
            </Button>
            
            <span className="text-slate-600">
              Page {currentPage} of {totalPages}
            </span>
            
            <Button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              variant="outline"
            >
              Next
            </Button>
          </div>
        )}

        {/* Shopping Cart Summary */}
        {cart.length > 0 && (
          <div 
            className="fixed bottom-6 right-6 bg-white border border-slate-200 rounded-xl shadow-lg p-4 max-w-sm cursor-pointer hover:shadow-xl transition-shadow"
            onClick={navigateToCart}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-800">Shopping Cart</h4>
              <Badge variant="outline">{cart.length} items</Badge>
            </div>
            
            <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
              {cart.map(item => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="truncate flex-1">{item.name}</span>
                  <span className="font-medium ml-2">{item.quantity}x</span>
                </div>
              ))}
            </div>
            
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold">Total:</span>
              <span className="font-bold text-lg">
                {formatPrice(cart.reduce((total, item) => total + (item.price * item.quantity), 0))}
              </span>
            </div>
            
            <Button 
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                navigateToCart();
              }}
            >
              Checkout ({cart.length})
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProductsPage;